import React from 'react';
import styled, { keyframes } from 'styled-components';

const AIWebinarFeaturesES = () => {
  const features = [
    {
      id: 1,
      icon: '⚙️',
      title: 'Software de IA que Ahorra Tiempo y Dinero',
      description: 'Construimos herramientas de IA personalizadas para empresas, como un sistema inteligente de caja que rastrea ventas e inventario automáticamente. Menos trabajo manual, más enfoque en el crecimiento.',
    },
    {
      id: 2,
      icon: '🌐',
      title: 'Sitios Web Inteligentes que Trabajan para Ti',
      description: '¿Necesitas funciones de reserva y pago? Ya seas mecánico, consultor o dueño de una tienda, creamos sitios web impulsados por IA que gestionan citas y pagos sin esfuerzo.',
    },
    {
      id: 3,
      icon: '🤖',
      title: 'Herramientas de IA que Puedes Usar Ahora',
      description: '¡No necesitas software personalizado! Enseñamos a los dueños de negocios cómo usar herramientas de IA existentes para servicio al cliente, marketing y automatización—ahorrando tiempo y dinero al instante.',
    },
    {
      id: 4,
      icon: '📈',
      title: 'Mantente Adelante de tu Competencia',
      description: 'La IA no es opcional—es el futuro. Te ayudamos a usar la IA estratégicamente para aumentar leads, automatizar tareas y crecer más rápido que tus competidores.',
    },
  ];

  return (
    <FeaturesContainer id="features">
      <SectionHeader>
        <FeatureBadge>LO QUE APRENDERÁS</FeatureBadge>
        <SectionTitle>Transforma tu Negocio con <HighlightText>TECNOLOGÍA IA</HighlightText></SectionTitle>
        <SectionDescription>
          Desde soluciones personalizadas hasta herramientas existentes, descubre cómo la IA puede <strong>revolucionar tus operaciones</strong> e impulsar el crecimiento de tu negocio.
        </SectionDescription>
      </SectionHeader>
      
      <FeaturesGrid>
        {features.map((feature) => (
          <FeatureCard key={feature.id}>
            <CardHeader>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureNumber>0{feature.id}</FeatureNumber>
            </CardHeader>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>
      
      <WebinarPromo>
        <PromoTitle>Webinar Gratuito de IA: Aprende, Construye, Crece</PromoTitle>
        <PromoDescription>
          ¿Quieres modernizar tu negocio con IA? Regístrate en nuestro webinar gratuito donde te mostramos cómo la IA puede transformar tu negocio—ya sea con software personalizado o herramientas existentes.
        </PromoDescription>
        <PromoCTA href="#webinar-signup">RESERVA TU LUGAR AHORA</PromoCTA>
      </WebinarPromo>
      
      <GradientDivider />
    </FeaturesContainer>
  );
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 255, 198, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Styled Components
const FeaturesContainer = styled.section`
  padding: 100px 80px;
  background-color: #0f1219;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 10% 30%, rgba(0, 255, 198, 0.05) 0%, transparent 70%);
    z-index: 0;
  }
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  z-index: 1;
`;

const FeatureBadge = styled.div`
  display: inline-block;
  background: rgba(0, 255, 198, 0.1);
  color: #00FFC6;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 255, 198, 0.2);
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  margin-top: 10px;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  line-height: 1.6;
  
  strong {
    color: #00FFC6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: linear-gradient(135deg, rgba(15, 18, 25, 0.95), rgba(21, 26, 36, 0.95));
  border-radius: 16px;
  padding: 35px;
  border: 1px solid rgba(0, 255, 198, 0.1);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(0, 255, 198, 0.3);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 255, 198, 0.2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 5px;
  filter: drop-shadow(0 0 10px rgba(0, 255, 198, 0.5));
`;

const FeatureNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  opacity: 0.3;
  color: #00FFC6;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: #a0a0a0;
  line-height: 1.6;
`;

const WebinarPromo = styled.div`
  background: linear-gradient(135deg, rgba(15, 18, 25, 0.9), rgba(21, 26, 36, 0.9));
  border-radius: 16px;
  padding: 60px;
  border: 1px solid rgba(0, 255, 198, 0.2);
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  margin: 80px 0 40px;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, transparent, rgba(0, 255, 198, 0.1), transparent);
    background-size: 1000px 100%;
    animation: ${shimmer} 5s infinite linear;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const PromoTitle = styled.h3`
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const PromoDescription = styled.p`
  font-size: 1.1rem;
  color: #a0a0a0;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 30px;
`;

const PromoCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  padding: 18px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 255, 198, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 255, 198, 0.4);
  }
`;

const GradientDivider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 198, 0.5), transparent);
  margin: 60px 0 0;
`;

export default AIWebinarFeaturesES; 